import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Globe2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminAutoReconcileForeignAdrsButton — Round 95j (Feb 2026).
 *
 * Batch-verifies every `origin=foreign_adr` row in `tier_list_cuts`
 * against Massive's home-currency dividend history. Any ADR whose
 * parent company kept raising its dividend in CHF / EUR / CAD / etc.
 * gets automatically deactivated — the "cut" was pure FX conversion
 * noise, not a real corporate policy change.
 *
 * Rate-limited by 300 ms per ticker to stay well under Massive's
 * generous limits. Takes ~15 seconds for the full ~37-ticker sweep.
 */
export default function AdminAutoReconcileForeignAdrsButton() {
  const { user } = useAuth();
  const [firing, setFiring] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  if (user?.role !== 'admin') return null;

  const handleFire = async () => {
    if (firing) return;
    setFiring(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/tier-cuts/auto-reconcile-foreign-adrs`,
        {},
        { withCredentials: true, timeout: 120 * 1000 },
      );
      setLastResult(data);
      toast.success('Foreign-ADR audit complete', {
        description: `Checked ${data.checked} · Deactivated ${data.deactivated.length} (home-currency intact) · Kept ${data.kept.length}`,
      });
    } catch (err) {
      toast.error('Auto-reconcile failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setFiring(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleFire}
        disabled={firing}
        data-testid="admin-auto-reconcile-foreign-adrs-fire"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: batch-verify every foreign ADR flagged as 'cut' against its parent company's home-currency dividend history via Massive. Deactivates rows where the CHF/EUR/CAD/etc. streak is still growing (FX conversion artifact, not a real cut). ~15 seconds for ~37 tickers."
      >
        {firing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe2 className="w-3 h-3" />}
        Auto-reconcile Foreign ADRs
      </button>
      {lastResult && (
        <span
          className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-300"
          data-testid="admin-auto-reconcile-foreign-adrs-status"
          title={
            `Checked: ${lastResult.checked}\n\n` +
            `Deactivated (home-currency streak intact):\n` +
            lastResult.deactivated.map((x) => `  ${x.ticker}: ${x.home_currency} ${x.home_streak_years}y`).join('\n') +
            `\n\nKept (real cut or no data):\n` +
            lastResult.kept.map((x) => `  ${x.ticker}: ${x.reason}`).join('\n')
          }
        >
          <CheckCircle2 className="w-3 h-3" />
          {lastResult.deactivated.length} deactivated · {lastResult.active_remaining} active
        </span>
      )}
    </div>
  );
}
